import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, theme } from 'antd';
import type { ClienteMapPoint } from '@aid/shared';
import type {} from 'leaflet.markercluster';
import { useKanagawaPalette } from '@aid/theme';
import { EmptyState } from '../../../components/EmptyState';
import { useLeaflet } from '../../../lib/useLeaflet';
import '../../../components/ver-mapa.css';
import { SectionHeader } from '../../../components/SectionHeader';
import { DashboardCard } from '../../../components/DashboardCard';
import { ClientesMapFilter, tieneFiltros } from './ClientesMapFilter';
import type {
  ClienteMapFilterOption,
  DiaCobroFiltro,
  EstadoCarteraFiltro,
  FacturasVencidasFiltro,
  TipoSuscripcionFiltro,
} from './ClientesMapFilter';
import { buildClientesMapPopup } from './ClientesMapPopup';
import { useT } from '../../../locales';

// Map geometry: GT's center at a country-wide zoom.
const MAP_CENTER: [number, number] = [15.5, -90.25];
const MAP_ZOOM = 7;
const MAP_FOCUS_ZOOM = 16;
const MAP_MAX_ZOOM = 19;
// Viewport-sized height: PageHeader + tabs bar + page paddings account for
// ~170px of chrome above the tab content, leaving ~80% of the viewport for
// the map on a typical 1080p screen.
const MAP_HEIGHT = 'calc(100dvh - 170px)';
const MAP_MIN_HEIGHT = 480;

// Cluster density tiers: count < 10 → tier 0, < 50 → 1, < 200 → 2, else 3.
const CLUSTER_MAX_RADIUS = 44;
const CLUSTER_COUNT_TIERS: readonly number[] = [10, 50, 200];
const CLUSTER_SIZE_TIERS: readonly number[] = [38, 44, 50, 56];
const CLUSTER_FONT_TIERS: readonly number[] = [13, 14, 15, 16];

// Map legibility colors that stay fixed on any palette: white borders/text
// on the colored badges and dark drop shadows for depth on the map tiles.
const MARKER_WHITE = '#fff';
const CLUSTER_SHADOW = '0 2px 8px rgba(0,0,0,0.6)';
const MARKER_SHADOW = '0 1px 4px rgba(0,0,0,0.5)';

function clusterTier(count: number): number {
  if (count < CLUSTER_COUNT_TIERS[0]!) return 0;
  if (count < CLUSTER_COUNT_TIERS[1]!) return 1;
  if (count < CLUSTER_COUNT_TIERS[2]!) return 2;
  return 3;
}

interface ClientesMapProps {
  points: ClienteMapPoint[];
}

function estadoCarteraDe(point: ClienteMapPoint): Exclude<EstadoCarteraFiltro, 'todos'> {
  const deuda = Number(point.deudaPendiente ?? '0');
  if (!Number.isFinite(deuda) || deuda <= 0) return 'alDia';

  const diasEnMora = Number(point.diasEnMora ?? 0);
  if (!Number.isFinite(diasEnMora) || diasEnMora <= 30) return 'leve';
  if (diasEnMora <= 60) return 'moderada';
  return 'severa';
}

function coincideFacturasVencidas(point: ClienteMapPoint, filtro: FacturasVencidasFiltro): boolean {
  if (filtro === 'todos') return true;

  const count = point.facturasVencidas ?? 0;
  if (filtro === 'cero') return count === 0;
  if (filtro === 'uno') return count === 1;
  if (filtro === 'dosTres') return count >= 2 && count <= 3;
  return count >= 4;
}

export function ClientesMap({ points }: ClientesMapProps) {
  const t = useT();
  const L = useLeaflet();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const { token } = theme.useToken();
  const palette = useKanagawaPalette();

  const [diaCobroFiltro, setDiaCobroFiltro] = useState<DiaCobroFiltro>(null);
  const [facturasVencidasFiltro, setFacturasVencidasFiltro] =
    useState<FacturasVencidasFiltro>('todos');
  const [estadoCarteraFiltro, setEstadoCarteraFiltro] = useState<EstadoCarteraFiltro>('todos');
  const [tipoSuscripcionFiltro, setTipoSuscripcionFiltro] =
    useState<TipoSuscripcionFiltro>('todos');
  const [clienteId, setClienteId] = useState<number | null>(null);

  const clientes = useMemo<ClienteMapFilterOption[]>(() => {
    const byId = new Map<number, ClienteMapFilterOption>();
    for (const point of points) {
      if (!byId.has(point.id)) {
        byId.set(point.id, { id: point.id, nombre: point.nombre });
      }
    }
    return [...byId.values()].sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [points]);

  // Client-side filters over the already-loaded points: no extra backend
  // round-trip, and the map effect below re-clusters whenever the filtered
  // array changes.
  const filteredPoints = useMemo(() => {
    return points.filter((pt) => {
      if (clienteId !== null && pt.id !== clienteId) return false;
      if (diaCobroFiltro !== null && pt.diaCobro !== diaCobroFiltro) return false;
      if (!coincideFacturasVencidas(pt, facturasVencidasFiltro)) return false;
      if (estadoCarteraFiltro !== 'todos' && estadoCarteraDe(pt) !== estadoCarteraFiltro) {
        return false;
      }
      if (tipoSuscripcionFiltro !== 'todos' && pt.tipoSuscripcionKey !== tipoSuscripcionFiltro) {
        return false;
      }
      return true;
    });
  }, [
    points,
    clienteId,
    diaCobroFiltro,
    facturasVencidasFiltro,
    estadoCarteraFiltro,
    tipoSuscripcionFiltro,
  ]);

  const filtrosActivos = tieneFiltros(
    clienteId,
    diaCobroFiltro,
    facturasVencidasFiltro,
    estadoCarteraFiltro,
    tipoSuscripcionFiltro,
  );
  const limpiarFiltros = () => {
    setClienteId(null);
    setDiaCobroFiltro(null);
    setFacturasVencidasFiltro('todos');
    setEstadoCarteraFiltro('todos');
    setTipoSuscripcionFiltro('todos');
  };

  useEffect(() => {
    if (!L || !mapRef.current) return;

    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    const map = L.map(mapRef.current, {
      center: MAP_CENTER,
      zoom: MAP_ZOOM,
      zoomControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: MAP_MAX_ZOOM,
    }).addTo(map);

    mapInstanceRef.current = map;

    // Cluster layer: groups nearby clients so 1.7k+ markers stay usable.
    // Custom iconCreateFunction overrides the plugin's default green CSS
    // with a deep blue that contrasts with OSM's green terrain. Density
    // hierarchy: the more clients in a cluster, the stronger the fill.
    // disableClusteringAtZoom >= 16 shows individual markers in dense areas
    // so big clusters never pile on top of each other.
    const clusterBase = token.colorPrimary;
    const clusterStrong = palette.blue;
    const clusterLayer = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: CLUSTER_MAX_RADIUS,
      spiderfyOnMaxZoom: true,
      chunkedLoading: true,
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        const tier = clusterTier(count);
        const size = CLUSTER_SIZE_TIERS[tier]!;
        const fontSize = CLUSTER_FONT_TIERS[tier]!;
        const fill = tier < 2 ? clusterBase : clusterStrong;
        const border = tier === 3 ? 2 : 3;
        return L.divIcon({
          className: '',
          html: `<div style="
            width: ${size}px; height: ${size}px; border-radius: 50%;
            background: ${fill};
            border: ${border}px solid ${MARKER_WHITE};
            box-shadow: ${CLUSTER_SHADOW};
            display: flex; align-items: center; justify-content: center;
            color: ${MARKER_WHITE}; font-weight: 700; font-size: ${fontSize}px;
            font-family: ui-sans-serif, system-ui, sans-serif;
            text-shadow: 0 1px 3px rgba(0,0,0,0.8);
            padding: 0 4px;
            box-sizing: border-box;
          ">${count}</div>`,
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
        });
      },
    });

    const bounds = L.latLngBounds([]);

    for (const pt of filteredPoints) {
      if (typeof pt.lat !== 'number' || typeof pt.lng !== 'number') continue;

      const iconColor = pt.anulado ? token.colorError : clusterStrong;
      const icon = L.divIcon({
        className: '',
        html: `<div style="
          width: 18px; height: 18px; border-radius: 50%;
          background: ${iconColor};
          border: 2.5px solid ${MARKER_WHITE};
          box-shadow: ${MARKER_SHADOW};
        "></div>`,
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      });

      const marker = L.marker([pt.lat, pt.lng], { icon });
      marker.bindPopup(
        buildClientesMapPopup(pt, t, {
          labelColor: token.colorTextSecondary,
          textColor: token.colorText,
          mutedColor: token.colorTextSecondary,
          borderColor: token.colorBorderSecondary,
          elevatedColor: token.colorBgElevated,
          primaryColor: token.colorPrimary,
          successColor: token.colorSuccess,
          warningColor: token.colorWarning,
          errorColor: token.colorError,
        }),
      );
      marker.bindTooltip(pt.nombre, { direction: 'top', offset: [0, -8] });
      marker.on('click', () => map.setView([pt.lat, pt.lng], MAP_FOCUS_ZOOM));

      clusterLayer.addLayer(marker);
      bounds.extend([pt.lat, pt.lng]);
    }

    clusterLayer.addTo(map);

    const locatedPoints = filteredPoints.filter(
      (pt) => typeof pt.lat === 'number' && typeof pt.lng === 'number',
    );
    if (locatedPoints.length === 1) {
      const [point] = locatedPoints;
      map.setView([point!.lat, point!.lng], MAP_FOCUS_ZOOM);
    } else if (locatedPoints.length > 1 && bounds.isValid()) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, [
    L,
    filteredPoints,
    token.colorPrimary,
    token.colorError,
    token.colorText,
    token.colorTextSecondary,
    token.colorTextTertiary,
    token.colorBorderSecondary,
    token.colorBgContainer,
    token.colorSuccess,
    token.colorWarning,
    palette.blue,
  ]);

  return (
    <DashboardCard
      headerStyle={{ padding: '14px 22px' }}
      bodyStyle={{ padding: 0 }}
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <SectionHeader
            title={t('dashboard.mapaTitle')}
            subtitle={
              filtrosActivos
                ? t('dashboard.mapaClientesDe', {
                    filtrados: filteredPoints.length,
                    total: points.length,
                  })
                : t('dashboard.mapaSubtitle')
            }
          />
          <ClientesMapFilter
            clienteId={clienteId}
            diaCobro={diaCobroFiltro}
            facturasVencidas={facturasVencidasFiltro}
            estadoCartera={estadoCarteraFiltro}
            tipoSuscripcion={tipoSuscripcionFiltro}
            clientes={clientes}
            filtrosActivos={filtrosActivos}
            onClienteChange={setClienteId}
            onDiaCobroChange={setDiaCobroFiltro}
            onFacturasVencidasChange={setFacturasVencidasFiltro}
            onEstadoCarteraChange={setEstadoCarteraFiltro}
            onTipoSuscripcionChange={setTipoSuscripcionFiltro}
            onLimpiar={limpiarFiltros}
          />
        </div>
      }
    >
      {filteredPoints.length === 0 ? (
        <div style={{ padding: '40px 24px' }}>
          <EmptyState
            compact
            title={t('dashboard.mapaSinResultados')}
            description={t('dashboard.mapaSinResultadosDesc')}
            action={<Button onClick={limpiarFiltros}>{t('dashboard.mapaLimpiarFiltros')}</Button>}
          />
        </div>
      ) : (
        <div
          ref={mapRef}
          className="ver-mapa clientes-map"
          style={{
            height: MAP_HEIGHT,
            minHeight: MAP_MIN_HEIGHT,
            width: '100%',
            borderRadius: '0 0 12px 12px',
            // Confine Leaflet's internal pane z-indexes (200-1000) to this
            // container so they never paint above the sticky header.
            isolation: 'isolate',
            // Reuse the theme-aware Leaflet popup/control treatment used by
            // VerMapaButton so the default white Leaflet shell never leaks
            // around this dark/light popup card.
            ['--mapa-popup-bg' as string]: token.colorBgContainer,
            ['--mapa-popup-text' as string]: token.colorText,
            ['--mapa-popup-border' as string]: token.colorBorderSecondary,
            ['--ver-mapa-control-bg' as string]: token.colorBgElevated,
            ['--ver-mapa-control-text' as string]: token.colorTextSecondary,
            ['--ver-mapa-control-border' as string]: token.colorBorderSecondary,
            ['--ver-mapa-control-hover-bg' as string]: token.colorFillSecondary,
          }}
        />
      )}
    </DashboardCard>
  );
}
